import { useMemo, useState } from "react";
import { FolderKanban, House, LoaderCircle, MessageSquarePlus, Pin, Search, ShieldCheck, Trash2, X } from "lucide-react";
import type { ProjectSummary, SessionSummary } from "../types";

interface SessionRailProps {
  open: boolean;
  sessions: SessionSummary[];
  projects: ProjectSummary[];
  projectFilterId: string | null;
  activeSessionId: string | null;
  busySessionId: string | null;
  onClose(): void;
  onCreate(projectId: string | null): void;
  onClearProjectFilter(): void;
  onSelect(sessionId: string): void;
  onDelete(session: SessionSummary): void;
}

export function SessionRail(props: SessionRailProps) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("fr");
    const projectSessions = props.projectFilterId
      ? props.sessions.filter((session) => session.projectId === props.projectFilterId)
      : props.sessions;
    if (!needle) return projectSessions;
    return projectSessions.filter((session) =>
      `${session.title} ${session.workspacePath} ${session.latestMessage}`.toLocaleLowerCase("fr").includes(needle)
    );
  }, [props.projectFilterId, props.sessions, query]);
  const activeProject = props.projects.find((project) => project.id === props.projectFilterId) ?? null;

  return (
    <>
      <button className={`drawer-backdrop ${props.open ? "open" : ""}`} type="button" onClick={props.onClose} aria-label="Fermer les sessions" />
      <aside className={`session-rail ${props.open ? "open" : ""}`} aria-label="Sessions">
        <header className="rail-brand">
          <div className="brand-lockup">
            <span className="nora-logo" aria-hidden="true" />
            <div><strong>Nora</strong><span>{activeProject?.name ?? "Conversations"}</span></div>
          </div>
          <button className="icon-command rail-close" type="button" onClick={props.onClose} aria-label="Fermer">
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="rail-controls">
          {activeProject ? (
            <div className="rail-project-filter">
              <FolderKanban aria-hidden="true" />
              <span><strong>{activeProject.name}</strong><small>{activeProject.workspacePath}</small></span>
              <button type="button" onClick={props.onClearProjectFilter} aria-label="Afficher toutes les conversations"><X aria-hidden="true" /></button>
            </div>
          ) : null}
          <div className="rail-tools">
            <label className="search-field">
              <Search aria-hidden="true" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} type="search" placeholder="Rechercher" />
            </label>
            <button className="icon-command primary" type="button" onClick={() => props.onCreate(props.projectFilterId)} aria-label="Nouvelle session" title="Nouvelle session">
              <MessageSquarePlus aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="session-list">
          {filtered.length ? filtered.map((session) => (
            <article className={`session-row ${session.isGeneral ? "general" : ""} ${session.id === props.activeSessionId ? "active" : ""}`} key={session.id}>
              <button className="session-main" type="button" onClick={() => props.onSelect(session.id)}>
                {session.isGeneral ? (
                  <span className={`session-home ${session.status}`} aria-hidden="true"><House /></span>
                ) : (
                  <span className={`status-dot ${session.status}`} aria-hidden="true" />
                )}
                <span className="session-copy">
                  <strong>{session.title}</strong>
                  <small>{session.latestMessage || session.workspacePath}</small>
                </span>
                {session.pendingApprovalCount ? (
                  <span
                    className="session-count approval"
                    aria-label={`${session.pendingApprovalCount} validation${session.pendingApprovalCount > 1 ? "s" : ""} en attente`}
                    title="Validation requise"
                  >
                    <ShieldCheck aria-hidden="true" />
                    <b>{session.pendingApprovalCount}</b>
                  </span>
                ) : <span className="session-count">{session.messageCount}</span>}
              </button>
              {session.isGeneral ? (
                <span className="session-pinned" title="Conversation épinglée" aria-label="Conversation épinglée">
                  <Pin aria-hidden="true" />
                </span>
              ) : (
                <button
                  className="session-delete"
                  type="button"
                  onClick={() => props.onDelete(session)}
                  disabled={Boolean(session.activeRunId) || props.busySessionId != null}
                  aria-label={`Supprimer ${session.title}`}
                  title="Supprimer"
                >
                  {props.busySessionId === session.id ? <LoaderCircle className="spin" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}
                </button>
              )}
            </article>
          )) : (
            <div className="rail-empty">Aucune session</div>
          )}
        </div>
        <footer className="rail-footer">
          <span>{filtered.length} session{filtered.length > 1 ? "s" : ""}</span>
          <span>{filtered.filter((session) => session.activeRunId).length} en cours</span>
          {filtered.some((session) => session.pendingApprovalCount) ? (
            <span>{filtered.reduce((total, session) => total + (session.pendingApprovalCount ?? 0), 0)} à valider</span>
          ) : null}
        </footer>
      </aside>
    </>
  );
}
